import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowDown, Play, Heart, Users, BookOpen, Utensils, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Lazy load 3D component
const TreeOfLife3D = lazy(() => import('../components/TreeOfLife3D'));

// Animated counter component
function CountUpNumber({ value, suffix = '', className, style }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, value]);

  const formatNumber = (num) => {
    if (num >= 1000) {
      return num.toLocaleString();
    }
    return num;
  };

  return (
    <span ref={ref} className={className} style={style}>
      {formatNumber(count)}{suffix}
    </span>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const [showContent, setShowContent] = useState(false);
  const containerRef = useRef(null);
  
  const ministryLinks = [
    { icon: BookOpen, title: t('home.ministryLinks.sermons'), desc: t('home.ministryLinks.sermonsDesc'), path: '/sermons', color: 'bg-burgundy' },
    { icon: Play, title: t('home.ministryLinks.podcast'), desc: t('home.ministryLinks.podcastDesc'), path: '/podcast', color: 'bg-purple' },
    { icon: Users, title: t('home.ministryLinks.programs'), desc: t('home.ministryLinks.programsDesc'), path: '/programs', color: 'bg-olive' },
    { icon: Utensils, title: t('home.ministryLinks.foodPantry'), desc: t('home.ministryLinks.foodPantryDesc'), path: '/pantries', color: 'bg-gold' },
    { icon: Heart, title: t('home.ministryLinks.donate'), desc: t('home.ministryLinks.donateDesc'), path: '/donate', color: 'bg-burgundy' },
  ];
  
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div ref={containerRef} className="relative overflow-x-hidden" data-testid="home-page">
      {/* Hero Section - Clean Design */}
      <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-cream via-cream to-cream/90" />
          {/* Decorative elements - hidden on small screens to prevent overflow */}
          <div className="hidden sm:block absolute top-1/4 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
          <div className="hidden sm:block absolute bottom-1/4 right-1/4 w-96 h-96 bg-burgundy/5 rounded-full blur-3xl" />
        </div>
        
        {/* 3D Tree of Life Animation */}
        <div className="absolute inset-0 z-5 opacity-30 pointer-events-none overflow-hidden">
          <Suspense fallback={null}>
            <TreeOfLife3D />
          </Suspense>
        </div>
        
        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="text-center max-w-4xl"
          >
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xs md:text-sm uppercase tracking-[0.3em] text-charcoal mb-4"
            >
              {t('home.welcome')}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="font-serif text-5xl md:text-7xl lg:text-8xl text-charcoal tracking-tight mb-6"
            >
              {t('home.title')}
              <span className="block text-burgundy">{t('home.subtitle')}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-charcoal/60 font-sans text-sm md:text-base max-w-md mx-auto mb-4"
            >
              {t('home.tagline')}
            </motion.p>
            
            {/* Tax Status Badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex justify-center mb-8"
            >
              <div className="inline-flex items-center gap-2 bg-burgundy/10 px-4 py-2 rounded-full">
                <Heart size={16} className="text-burgundy" />
                <span className="text-sm font-medium text-charcoal">508(c)(1)(a) Religious Organization</span>
              </div>
            </motion.div>
            
            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/im-new"
                className="px-8 py-4 bg-burgundy text-white rounded-full font-medium hover:bg-purple transition-colors"
              >
                {t('nav.imNew')}
              </Link>
              <Link
                to="/watch"
                className="px-8 py-4 border-2 border-gold text-charcoal rounded-full font-medium hover:bg-gold/10 transition-colors flex items-center justify-center gap-2"
              >
                <Play size={18} /> {t('nav.watch')}
              </Link>
            </motion.div>
          </motion.div>
          
          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          >
            <span className="text-xs uppercase tracking-widest text-charcoal/50">{t('home.scrollToGrow', 'Explore')}</span>
            <ArrowDown className="scroll-indicator text-gold" size={24} />
          </motion.div>
        </div>
      </section>
      
      {/* Ministry Links Section */}
      <section className="relative z-20 bg-white py-16" data-testid="ministry-links-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-center gap-6 md:gap-10 flex-wrap">
            {ministryLinks.map((link, i) => (
              <Link
                key={link.title}
                to={link.path}
                data-testid={`ministry-link-${i}`}
                className="group flex flex-col items-center"
              >
                <div className={`${link.color} p-5 rounded-2xl mb-3 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg`}>
                  <link.icon size={28} className="text-white" />
                </div>
                <span className="text-sm font-medium text-charcoal">{link.title}</span>
                <span className="text-xs text-charcoal/50">{link.desc}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Live Impact Counter */}
      <section className="relative z-20 py-16" style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #1E3A5F 100%)' }} data-testid="impact-counter-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-block px-4 py-1 rounded-full text-xs uppercase tracking-widest mb-4"
              style={{ background: 'rgba(212, 149, 106, 0.2)', color: '#d4956a' }}>
              Our Impact
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              Making a Difference in Central Florida
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { value: 1247, suffix: '+', label: 'Families Fed This Year', icon: '🍞' },
              { value: 52, suffix: '', label: 'Weekly Food Distributions', icon: '📦' },
              { value: 15, suffix: '+', label: 'Years Serving Community', icon: '🏛️' },
              { value: 25000, suffix: '+', label: 'Meals Provided', icon: '🍽️' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <span className="text-4xl mb-3 block">{stat.icon}</span>
                <CountUpNumber 
                  value={stat.value} 
                  suffix={stat.suffix}
                  className="font-serif text-4xl md:text-5xl font-bold"
                  style={{ color: '#d4956a' }}
                />
                <span className="text-white/70 text-sm mt-2 block">{stat.label}</span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center mt-10"
          >
            <Link
              to="/impact"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
            >
              <span>Read Impact Stories</span>
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Mission Section */}
      <section className="relative z-20 bg-cream py-24 md:py-32" data-testid="mission-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-charcoal mb-4 block">{t('home.ourMission')}</span>
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal mb-8 leading-tight">
              {t('home.missionTitle')}
            </h2>
            <p className="text-charcoal/70 font-sans text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              {t('home.missionText')}
            </p>
          </motion.div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
            {[
              { number: '500+', label: t('home.familiesServed') },
              { number: '10+', label: t('home.programs') },
              { number: '50+', label: t('home.volunteers') },
              { number: '2024', label: t('home.established') },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <span className="block font-serif text-4xl md:text-5xl text-burgundy">{stat.number}</span>
                <span className="text-sm text-charcoal/60 mt-2 block">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Featured Programs */}
      <section className="relative z-20 bg-white py-24" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-gold mb-2 block">{t('home.whatWeDo')}</span>
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal">{t('home.ourPrograms')}</h2>
            </div>
            <Link 
              to="/programs" 
              className="flex items-center gap-2 text-burgundy hover:text-purple transition-colors"
              data-testid="view-all-programs"
            >
              {t('home.viewAll')} <ChevronRight size={18} />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: t('home.foodPantry'),
                desc: t('home.foodPantryDesc'),
                image: 'https://images.unsplash.com/photo-1599659593072-10de2e109486?w=600'
              },
              {
                title: t('home.worshipServices'),
                desc: t('home.worshipServicesDesc'),
                image: 'https://images.unsplash.com/photo-1655392032737-6a4fabe2f15b?w=600'
              },
              {
                title: t('home.communityOutreach'),
                desc: t('home.communityOutreachDesc'),
                image: 'https://images.unsplash.com/photo-1725596854669-61fb4badaf4d?w=600'
              }
            ].map((program, i) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="ministry-card bg-cream rounded-2xl overflow-hidden border border-gold/10"
                data-testid={`program-card-${i}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={program.image} 
                    alt={program.title}
                    className="w-full h-full object-cover transform transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl text-charcoal mb-2">{program.title}</h3>
                  <p className="text-charcoal/60 text-sm">{program.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="relative z-20 bg-burgundy py-20" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-6">
              {t('home.bePartOf')}
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              {t('home.ctaText')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/donate"
                className="bg-gold text-charcoal px-8 py-4 rounded-full font-sans uppercase tracking-widest text-xs hover:bg-white transition-colors"
                data-testid="cta-donate"
              >
                {t('home.giveToday')}
              </Link>
              <Link
                to="/volunteer"
                className="bg-transparent border border-white text-white px-8 py-4 rounded-full font-sans uppercase tracking-widest text-xs hover:bg-white hover:text-burgundy transition-colors"
                data-testid="cta-volunteer"
              >
                {t('nav.volunteer')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
